import NavigationLink from '../../interfaces/NavigationLink';

/**
 * Global navigation hotkeys.
 *
 * The active map is owned by HotkeySettingsProvider (localStorage-backed and
 * user-editable). The helpers here are pure and take the current hotkey list so
 * they can be reused by the listener hook, the sidebar hints and the config UI.
 */
export interface Hotkey {
  /** KeyboardEvent.code, layout independent (e.g. 'KeyL', 'Digit1'). */
  code: string;
  alt?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  /** Absolute route to navigate to. */
  path: string;
  /** Human label for the destination. */
  label: string;
}

export interface HotkeySettings {
  /** Master switch. Off by default — nothing is active or visible. */
  enabled: boolean;
  /** Whether to render the combo hints next to the sidebar items. */
  showBadges: boolean;
  hotkeys: Hotkey[];
}

export const DEFAULT_HOTKEYS: Hotkey[] = [
  { code: 'KeyL', alt: true, path: '/leads', label: 'Manage Leads' },
  { code: 'KeyJ', alt: true, path: '/journeys', label: 'Journey Builder' },
  {
    code: 'KeyC',
    alt: true,
    path: '/customer-cube/customer-360',
    label: 'Customer 360',
  },
  { code: 'KeyO', alt: true, path: '/offers', label: 'Offer Listing' },
  { code: 'KeyS', alt: true, path: '/settings/preferences', label: 'Settings' },
];

export const DEFAULT_HOTKEY_SETTINGS: HotkeySettings = {
  enabled: false,
  showBadges: true,
  hotkeys: DEFAULT_HOTKEYS,
};

/** Combos owned by other features — not assignable to a page. */
export const RESERVED_COMBOS: { code: string; ctrl?: boolean; alt?: boolean }[] =
  [
    { code: 'KeyK', ctrl: true }, // instant search
    { code: 'Slash', ctrl: true }, // open this dialog
  ];

/** Stable key for comparing two combos (modifiers + code). */
export const comboKey = (
  hotkey: Pick<Hotkey, 'code' | 'alt' | 'ctrl' | 'shift'>
): string =>
  `${!!hotkey.ctrl}-${!!hotkey.alt}-${!!hotkey.shift}-${hotkey.code}`;

/** Human-readable combo, e.g. 'Alt+L'. */
export const formatHotkey = (
  hotkey: Pick<Hotkey, 'code' | 'alt' | 'ctrl' | 'shift'>
): string => {
  const parts: string[] = [];
  if (hotkey.ctrl) parts.push('Ctrl');
  if (hotkey.alt) parts.push('Alt');
  if (hotkey.shift) parts.push('Shift');
  parts.push(hotkey.code.replace(/^Key/, '').replace(/^Digit/, ''));
  return parts.join('+');
};

export const isReservedCombo = (
  hotkey: Pick<Hotkey, 'code' | 'alt' | 'ctrl' | 'shift'>
): boolean =>
  RESERVED_COMBOS.some(
    (reserved) =>
      reserved.code === hotkey.code &&
      !!reserved.ctrl === !!hotkey.ctrl &&
      !!reserved.alt === !!hotkey.alt
  );

const normalizePath = (path: string): string =>
  path
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '')
    .replace(/^\//, '');

/** Returns the formatted combo for a given route path, if one is assigned. */
export const getHotkeyLabelForPath = (
  path: string,
  hotkeys: Hotkey[]
): string | undefined => {
  const target = normalizePath(path);
  const match = hotkeys.find((hotkey) => normalizePath(hotkey.path) === target);
  return match ? formatHotkey(match) : undefined;
};

/**
 * Returns the first hotkey combo found within a nav item — either on the item
 * itself or, for a group, on any of its descendants. Lets a collapsed parent
 * surface the shortcut of a child page.
 */
export const getHotkeyLabelForNav = (
  item: NavigationLink,
  hotkeys: Hotkey[]
): string | undefined => {
  if (typeof item.content === 'string') {
    return getHotkeyLabelForPath(item.content, hotkeys);
  }
  for (const child of item.content) {
    const label = getHotkeyLabelForNav(child, hotkeys);
    if (label) return label;
  }
  return undefined;
};

export const hotkeyMatchesEvent = (
  event: KeyboardEvent,
  hotkey: Hotkey
): boolean =>
  event.code === hotkey.code &&
  event.altKey === !!hotkey.alt &&
  event.ctrlKey === !!hotkey.ctrl &&
  event.shiftKey === !!hotkey.shift &&
  !event.metaKey;

/** True when the user is typing into a field, so hotkeys must not fire. */
export const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable
  );
};

/** A combo is valid only with at least one modifier. */
export const hasModifier = (
  hotkey: Pick<Hotkey, 'alt' | 'ctrl' | 'shift'>
): boolean => !!(hotkey.alt || hotkey.ctrl || hotkey.shift);
